import { useEffect, useState } from "react";
import useApi from "./useApi";
import { getPostIfPositionRules } from "../services/pifService";
import { PostIfPositionRulesDto } from "../types/pifDto";

interface UsePifPositionRulesArgs {
  departmentId: number | null;
  positionId: number | null;
}

/**
 * Loads the show/hide/lock rules and pay bands for the chosen department + position.
 *
 * Null until both are chosen — which is the signal the sections use to stay collapsed and
 * the pay band uses to say "pick a position first". A band value can itself come back null
 * when HR_PositionRates has no row for the position; callers must treat that as "unknown",
 * never as zero, or every rate would read as out of band.
 */
const usePifPositionRules = ({ departmentId, positionId }: UsePifPositionRulesArgs) => {
  const { executeApi } = useApi();
  const [rules, setRules] = useState<PostIfPositionRulesDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!departmentId || !positionId) {
        setRules(null);
        return;
      }
      setLoading(true);
      const result = await executeApi<{ departmentId: number; positionId: number }, PostIfPositionRulesDto>(
        getPostIfPositionRules,
        { departmentId, positionId },
      );
      if (!active) return;
      setRules(result);
      setLoading(false);
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi, departmentId, positionId]);

  return { rules, loading };
};

export default usePifPositionRules;
